<script setup lang="ts">
import { Button, ZhPageTitle, ZhPanel } from '@zerohuman/ui'
import { computed, onMounted, ref } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { api, type Member, type Role, type TaskRow } from '@/api'
import DataTable, { type DataTableColumn } from '@/components/DataTable.vue'
import FirstRunHint from '@/components/FirstRunHint.vue'
import { useClientTable } from '@/composables/useClientTable'
import { useListState } from '@/composables/useListState'
import { runsAsRole } from '@/lib/taskRoles'

/** A role row carries the counts the old cards computed, so the table can sort on them. */
type RoleRow = Role & { memberCount: number; taskCount: number }

const router = useRouter()
const roles = ref<Role[]>([])
const members = ref<Member[]>([])
const tasks = ref<TaskRow[]>([])
const loading = ref(true)
const error = ref<string | null>(null)

const rows = computed<RoleRow[]>(() =>
    roles.value.map(role => ({
        ...role,
        memberCount: holders(role).length,
        taskCount: tasksOf(role).length,
    })),
)

/** Loaded, no error, and no role at all: the page has never had anything on it, not a search narrowed to zero. */
const noRoles = computed(() => !loading.value && !error.value && roles.value.length === 0)

const table = useClientTable({
    data: rows,
    searchFields: ['name', 'slug'],
    defaultSort: 'name',
    defaultOrder: 'ASC',
})
/** #633: the table's search, sort and filters come back on the next visit. */
const list = useListState('roles', table.listFields)

const columns: DataTableColumn[] = [
    { key: 'name', label: 'Role', sort: 'name', type: 'text' },
    { key: 'slug', label: 'Slug', sort: 'slug', type: 'text' },
    { key: 'memberCount', label: 'Holders', sort: 'memberCount', type: 'number', filterable: false },
    { key: 'taskCount', label: 'Tasks', sort: 'taskCount', type: 'number', filterable: false },
]

onMounted(async () => {
    try {
        roles.value = await api.roles()
        members.value = await api.members()
        tasks.value = (await api.tasks({ limit: 200 })).data
        error.value = null
    } catch {
        error.value = 'Could not load roles.'
    } finally {
        loading.value = false
    }
})

function holders(role: Role): Member[] {
    return members.value.filter(member => member.roles.some(held => held.id === role.id))
}

/** The tasks that run as this role: on its team, or on any team without its own role of that name when it has none. */
function tasksOf(role: Role): TaskRow[] {
    return tasks.value.filter(task => runsAsRole(task, roles.value)?.id === role.id)
}

function open(row: Record<string, unknown>): void {
    void router.push(`/roles/${String(row.id)}`)
}
</script>

<template>
	<section class="w-full space-y-4 p-8">
		<div class="flex items-center justify-between gap-4">
			<ZhPageTitle>Roles</ZhPageTitle>
			<Button as="a" href="/roles/new" data-testid="create-button" @click.prevent="router.push('/roles/new')">
				Create
			</Button>
		</div>
		<p class="text-muted-foreground text-sm">
			A role is a job across the company. Members hold it on any team. Each run of a task goes to a holder of its role, and every team where someone holds it runs the task.
		</p>
		<p v-if="error" class="text-destructive text-sm">{{ error }}</p>
		<!-- No roles yet: say what will appear here and offer the one action that adds one. -->
		<FirstRunHint
			v-if="noRoles"
			lead="No roles yet. The roles your members hold and your tasks run as appear here."
			:action="{ label: 'Create a role', to: '/roles/new' }"
		/>
		<!-- The roles, on a kit surface of their own; the table draws its own cell padding, so the panel is flush. -->
		<ZhPanel v-else flush>
			<DataTable
				storage-key="roles"
				:columns="columns"
				:loading="loading"
				:list-state="list"
				v-bind="table.tableProps.value"
				@row-click="open"
			>
				<template #cell-name="{ row }">
					<RouterLink class="font-medium underline-offset-2 hover:underline" :to="`/roles/${row.id}`">
						{{ row.name }}
					</RouterLink>
				</template>
				<template #cell-memberCount="{ value }">
					{{ value }} member{{ Number(value) === 1 ? '' : 's' }}
				</template>
				<template #cell-taskCount="{ value }">
					{{ value }} task{{ Number(value) === 1 ? '' : 's' }}
				</template>
			</DataTable>
		</ZhPanel>
	</section>
</template>
